import React, { useEffect } from 'react';
import { useGetSessionId } from 'src/components/kiosk-pro/kiosk-pro-shared/use-session-id';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import ArrowLeft from 'src/assets/arrow-left';

import {
  PhoneNumberContainer,
  Title,
  PhoneNumberInputWrapper,
  PhoneNumberPadWrapper,
  PhoneNumberDisplay,
  PhoneNumberText,
  BlinkingCursor,
  ReadOnlyInput,
  PhoneNumberPadGrid,
  BottomRow,
  Number,
  EnterButton,
} from './phone-number-pad.styles';
import { usePhoneNumberPad } from './use-phone-number-pad';
import { usePhoneNumberLogin } from './use-phone-number-login';
import { PhoneNumberLoginSuccess } from '../phone-number-login-success';

type PhoneNumberPadProps = {
  className?: string;
  onClose?: () => void;
  onLoginSuccess?: () => void;
  onResetLogin?: () => void;
};

/**
 * Phone number pad component for kiosk login.
 * Allows users to enter their phone number to login.
 */
export function PhoneNumberPad({ className, onClose, onLoginSuccess, onResetLogin }: PhoneNumberPadProps): JSX.Element {
  const { dispensary } = useDispensary();
  const cName = String(dispensary?.cName ?? '');
  const kioskMenuUrl = `/kiosks/${cName}`;

  const { phoneNumber, inputRef, handleNumberClick, showCursor, formattedPhone, digits } = usePhoneNumberPad();
  const sessionId = useGetSessionId();
  const {
    handlePhoneLogin,
    resetLoginState,
    isLoading,
    isSuccess,
    isError,
    loginState,
    userFirstName,
  } = usePhoneNumberLogin(sessionId);

  const handleEnterClick = async (): Promise<void> => {
    if (digits.length !== 10 || isLoading) {
      return;
    }

    await handlePhoneLogin(phoneNumber);
  };

  useEffect(() => {
    if (isSuccess && onLoginSuccess) {
      onLoginSuccess();
    }
  }, [isSuccess, onLoginSuccess]);

  const handleResetLoginState = (): void => {
    resetLoginState();
    onResetLogin?.();
  };

  // Show success message
  if (isSuccess) {
    return (
      <PhoneNumberLoginSuccess
        className={className}
        kioskMenuUrl={kioskMenuUrl}
        onClose={onClose}
        onResetLoginState={handleResetLoginState}
        userFirstName={userFirstName}
      />
    );
  }

  return (
    <PhoneNumberContainer className={className}>
      <Title>{isError && loginState.errorMessage ? loginState.errorMessage : 'Enter phone number'}</Title>
      <PhoneNumberInputWrapper>
        <PhoneNumberDisplay>
          <PhoneNumberText>{formattedPhone}</PhoneNumberText>
          {showCursor && <BlinkingCursor>|</BlinkingCursor>}
        </PhoneNumberDisplay>
        <ReadOnlyInput ref={inputRef} type='tel' id='phone-number' value={formattedPhone} readOnly />
      </PhoneNumberInputWrapper>

      <PhoneNumberPadWrapper>
        <PhoneNumberPadGrid>
          {Array.from({ length: 9 }, (_, i) => i + 1).map((number) => (
            <Number
              key={number}
              value={number}
              role='button'
              tabIndex={0}
              onClick={() => handleNumberClick(number)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleNumberClick(number);
                }
              }}
            >
              {number}
            </Number>
          ))}
        </PhoneNumberPadGrid>
        <BottomRow>
          <Number
            value={0}
            role='button'
            tabIndex={0}
            onClick={() => handleNumberClick(0)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleNumberClick(0);
              }
            }}
          >
            0
          </Number>
          <Number
            value='<-'
            role='button'
            tabIndex={0}
            onClick={() => handleNumberClick('<-')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleNumberClick('<-');
              }
            }}
          >
            <ArrowLeft />
          </Number>
        </BottomRow>
      </PhoneNumberPadWrapper>

      <EnterButton disabled={digits.length !== 10 || isLoading} onClick={handleEnterClick}>
        {isLoading ? 'Signing in...' : 'Enter'}
      </EnterButton>
    </PhoneNumberContainer>
  );
}
